
/* -----------------------------------------------------------------------------------------------------
  - Created: [20-02-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains EmployeeUtilizationTable using MUI components
  ------------------------------------------------------------------------------------------------------- */

import React, { useEffect, useState } from 'react';
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination';
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable';
import { get_employee_utilization_reports_with_pagination } from '../../../../config/ReportApis/employeeUtilizationApis';
import { handlePaginationEntries } from '../../../../utils/pagination';
import EmployeeUtilizationFilter from '../EmployeeUtilizationFilter/EmployeeUtilizationFilter';
import Loader from '../../../../Components/Loader/Loader';
import useDataExport from '../../../../utils/useDataExport';

function EmployeeUtilizationTable() {

    const [EmployeeUtilizationList, setSEmployeeUtilizationList] = useState([]);

    // Calculate the total pages based on the number of items and items per page
    const [pageNumber, setPageNumber] = useState(1);
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [pagination, setPagination] = useState({ start: 0, end: 0, totalEntries: 0 });

    const [filterValues, setFilterValues] = useState({}); // Define filter values state
    const [isLoading, setIsLoading] = useState(false); // Loader state


    // Columns for the table
    const columns = [
        { title: "#", key: "key" },
        { title: "Employee Name", key: "Employee_Name" },
        { title: "Used Days", key: "UsedDays" },
        { title: "Total Days", key: "TotalAvailableDays" },
        { title: "Utilization Percent", key: "UtilizationPercent" },
        {
            title: "Utilization",
            key: "LessUtilized",
            render: (LessUtilized) => (

                <span style={{ color: LessUtilized ? '#0A9E02' : '#FF0000' }}>
                    {LessUtilized ? 'Low usage ' : 'High usage'}
                </span>
            ),
        },

    ]

    // Map the schedule data into table rows
    const rows = () => EmployeeUtilizationList?.map((item, index) => ({
        key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
        Employee_Name: item.EmployeeName,
        LessUtilized: item.LessUtilized,
        UsedDays: item?.UsedDays,
        UtilizationPercent: `${item?.UtilizationPercent}%`,
        TotalAvailableDays: item?.TotalAvailableDays,

    }));
    const headers = columns.map(col => col.title);

    const mapDataFn = (item, index) => [
        index + 1,
        item?.EmployeeName || "—",
        item?.UsedDays || "—",
        item?.TotalAvailableDays || "—",
        `${item?.UtilizationPercent}%`,
        item?.LessUtilized ? 'Low usage ' : 'High usage' || "—",

    ];
    const {
        handleExportExcel,
        handleExportPDF,
        isExportingExcel,
        isExportingPDF,
    } = useDataExport(
        get_employee_utilization_reports_with_pagination,
        mapDataFn,
        headers,
        "Employee Utilization Report"
    );




    // Fetch schedule data on page change or refresh
    useEffect(() => {
        getFutureScheduleReport();
    }, [pageNumber, totalItemPerPage, filterValues]);


    // Fetch schedule data from API
    const getFutureScheduleReport = async () => {
        const formData = new FormData();
        formData.append('page', pageNumber);
        formData.append('pageItemcount', totalItemPerPage);

        // Append filter values to formData
        Object.keys(filterValues).forEach(key => formData.append(key, filterValues[key]));

        try {
            setIsLoading(true)
            const response = await get_employee_utilization_reports_with_pagination(formData);
            if (response?.status === "success") {
                const data = response?.data;
                setSEmployeeUtilizationList(data?.data);
                setTotalPages(data.last_page);
                setPagination(handlePaginationEntries(data.from, data.to, data.total));
            } else {
                setSEmployeeUtilizationList([]);
            }
        } catch (error) {

        } finally {
            setIsLoading(false)
        }
    };

    // Handle search filter change
    const handleSearchFilter = (filterValues) => {
        setFilterValues(filterValues);
    };

    // Handle pagination changes
    const handlePageChange = (pageNumber) => setPageNumber(pageNumber);
    const handleItemPageCountChange = (pageItemCount) => setTotalItemPerPage(pageItemCount);


    return (
        <div className='report-table-container'>
            <EmployeeUtilizationFilter
                tableTitle="Employee Utilization Report"
                searchFilter={handleSearchFilter}

                on_change_value={handlePageChange}
                on_page_count_update={handleItemPageCountChange}

                onExportPDF={() => handleExportPDF(filterValues, { page: 1, pageItemCount: pagination?.totalEntries })}
                onExportExcel={() => handleExportExcel(filterValues, { page: 1, pageItemCount: pagination?.totalEntries })}
                isExportingPDF={isExportingPDF}
                isExportingExcel={isExportingExcel}


            />
            <div className="table-container">
                <CustomTable
                    columns={columns}
                    rows={isLoading ? [] : rows()}
                    isCheckbox={false}
                    isLoading={isLoading}
                />
            </div>
            <div className='bottom-container'>
                {isLoading ? <Loader /> : <CustomBottomPagination
                    items_per_page={totalItemPerPage}
                    current_page={pageNumber}
                    total_page_count={totalPages}
                    on_change_value={handlePageChange}
                    on_page_count_update={handleItemPageCountChange}
                    pagination={pagination}

                />}
            </div>
        </div>
    );
}

export default EmployeeUtilizationTable;
